import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { IStudentPhoto } from 'models/estudiante_foto_model';
import type { Area } from 'react-easy-crop';
import {
  getStudentPhotosService,
  rejectStudentPhotoService,
  approveStudentPhotoService,
  createStudentPhotoService,
} from 'repositories/estudiante_foto_repository';

export interface FiltroCargarEstudiantes {
  idEstudiante?: string;
  curso?: string;
  gestion?: string;
  codigoEstudiante?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  _limit?: number;
  _page?: number;
}

interface EstudianteFotoState {
  studentPhotos: IStudentPhoto[] | null;
  countStudentPhotos: number;
  loading: boolean;
  error: string | null;
}

const initialState: EstudianteFotoState = {
  studentPhotos: null,
  countStudentPhotos: 0,
  loading: false,
  error: null,
};

export const getStudentPhotos = createAsyncThunk(
  'estudianteFoto/getStudentPhotos',
  async (
    {
      bandeja,
      filtros,
    }: { bandeja: 'recibidos' | 'aprobados' | 'rechazados'; filtros?: FiltroCargarEstudiantes },
    { rejectWithValue },
  ) => {
    try {
      return await getStudentPhotosService(bandeja, filtros);
    } catch {
      return rejectWithValue('Error al cargar fotos de estudiantes');
    }
  },
);

export const rejectStudentPhoto = createAsyncThunk(
  'estudianteFoto/rejectStudentPhoto',
  async (
    data: { idStudent: string; motivoRejection: string; idSolicitud: string },
    { rejectWithValue },
  ) => {
    try {
      return await rejectStudentPhotoService(data.idSolicitud, data.idStudent, data.motivoRejection);
    } catch {
      return rejectWithValue('Error al rechazar foto');
    }
  },
);

export const approveStudenPhoto = createAsyncThunk(
  'estudianteFoto/approveStudentPhoto',
  async (
    data: { idStudent: string; idSolicitud: string; crop?: Area },
    { rejectWithValue },
  ) => {
    try {
      return await approveStudentPhotoService(data.idSolicitud, data.idStudent, data.crop);
    } catch {
      return rejectWithValue('Error al aprobar foto');
    }
  },
);

export const createStudentPhoto = createAsyncThunk(
  'estudianteFoto/createStudentPhoto',
  async (data: { idStudent: string; image: Blob }, { rejectWithValue }) => {
    try {
      return await createStudentPhotoService(data.idStudent, data.image as File);
    } catch {
      return rejectWithValue('Error al crear foto de estudiante');
    }
  },
);

export const estudianteFotoSlice = createSlice({
  name: 'estudianteFoto',
  initialState,
  reducers: {
    setListStudentPhotos: (
      state,
      action: PayloadAction<{ rows: IStudentPhoto[]; count: number } | IStudentPhoto[]>,
    ) => {
      if (Array.isArray(action.payload)) {
        state.studentPhotos = action.payload;
        state.countStudentPhotos = action.payload.length;
      } else {
        state.studentPhotos = action.payload.rows;
        state.countStudentPhotos = action.payload.count;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStudentPhotos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getStudentPhotos.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload as { rows: IStudentPhoto[]; count: number } | IStudentPhoto[];
        if (Array.isArray(data)) {
          state.studentPhotos = data;
          state.countStudentPhotos = data.length;
        } else {
          state.studentPhotos = data.rows ?? [];
          state.countStudentPhotos = data.count ?? 0;
        }
      })
      .addCase(getStudentPhotos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setListStudentPhotos } = estudianteFotoSlice.actions;
export default estudianteFotoSlice.reducer;
